/**
 * EmailTemplateRenderer — renders HTML and plain-text email
 * templates with variable interpolation, conditional sections,
 * lists, buttons, links, and formatted dates.
 *
 * The renderer is deliberately simple — no external templating
 * engine dependency. It supports a curated set of features
 * sufficient for transactional emails.
 */

import type { BrandConfig, EmailContext, RenderedTemplate } from './types';
import { getBrandConfig } from './brand';
import { RenderingFailedError } from './errors';

export type TemplateRenderFn = (ctx: EmailContext, brand: BrandConfig) => RenderedTemplate;

const TEMPLATE_REGISTRY = new Map<string, TemplateRenderFn>();

export function registerTemplate(name: string, fn: TemplateRenderFn): void {
  TEMPLATE_REGISTRY.set(name, fn);
}

export function getRegisteredTemplates(): string[] {
  return Array.from(TEMPLATE_REGISTRY.keys());
}

export function hasTemplate(name: string): boolean {
  return TEMPLATE_REGISTRY.has(name);
}

export function renderTemplate(
  name: string,
  context: EmailContext = {},
  brand?: BrandConfig,
): RenderedTemplate {
  const fn = TEMPLATE_REGISTRY.get(name);
  if (!fn) {
    throw new RenderingFailedError(name, 'template not registered');
  }
  try {
    return fn(context, brand ?? getBrandConfig());
  } catch (err) {
    throw new RenderingFailedError(
      name,
      err instanceof Error ? err.message : String(err),
    );
  }
}

/**
 * Interpolate {{variable}} placeholders in a string.
 * Supports dot-notation paths: {{user.name}} -> ctx.user.name
 */
export function interpolate(template: string, ctx: EmailContext): string {
  return template.replace(/\{\{([^}]+)\}\}/g, (_match, key: string) => {
    const trimmed = key.trim();
    const value = resolvePath(ctx, trimmed);
    return value !== undefined && value !== null ? String(value) : '';
  });
}

function resolvePath(ctx: EmailContext, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = ctx;
  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function conditional(condition: boolean, content: string): string {
  return condition ? content : '';
}

export function renderList(items: string[], template: string): string {
  return items.map((item) => interpolate(template, { item })).join('');
}

export function renderButton(label: string, url: string, color: string): string {
  return `<a href="${url}" style="display:inline-block;padding:12px 32px;background-color:${color};color:#ffffff;text-decoration:none;border-radius:6px;font-weight:600;font-size:16px;">${label}</a>`;
}

export function renderButtonText(label: string, url: string): string {
  return `${label}: ${url}`;
}
